import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import CollectionErrorState from "@/components/collections/CollectionErrorState";
import ProductFilterBar from "@/components/collections/ProductFilterBar";
import ProductCard from "@/components/products/ProductCard";
import ProductDetailSlideIn from "@/components/products/ProductDetailSlideIn";
import ProductEmptyState from "@/components/products/ProductEmptyState";
import { useCollectionsContext } from "@/context/CollectionsContext";
import { useCollectionTierProducts } from "@/hooks/useCollectionTierProducts";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { usePermissions } from "@/hooks/usePermissions";
import { useProductDetails } from "@/hooks/useProductDetails";
import { useProductFilters } from "@/hooks/useProductFilters";
import type { Product } from "@/interfaces/product";
import { orderCollectionProducts } from "@/utils/product-helper";

// ─── Loading skeleton ──────────────────────────────────────────────────────────

const SKELETON_KEYS = ["a", "b", "c", "d", "e", "f", "g", "h"];

function ProductGridSkeleton({ isMobile }: { isMobile: boolean }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
        gap: 3,
      }}
    >
      {SKELETON_KEYS.map((key) => (
        <Box key={key} sx={{ borderRadius: "16px", overflow: "hidden" }}>
          <Skeleton variant="rectangular" height={264} sx={{ bgcolor: "#f0f0f0" }} />
          <Box sx={{ px: "12px", py: "16px", display: "flex", flexDirection: "column", gap: 1 }}>
            <Skeleton variant="rounded" width={90} height={20} sx={{ bgcolor: "#f0f0f0" }} />
            <Skeleton variant="text" width="70%" sx={{ bgcolor: "#f0f0f0" }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────
// Products for one budget tier — the SAME composed feed as Campaign Builder Step 1 (view mode):
// getCollectionsByBudget → composeForCountry("US"). Reuses ProductCard + the product slide-in.

export default function CollectionTier() {
  const { budget: budgetParam } = useParams<{ budget: string }>();
  const budget = Number(budgetParam);

  const isMobile = useIsMobile();

  // Slide-in state
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [slideInOpen, setSlideInOpen] = useState(false);

  const {
    data: detailedProduct = null,
    isLoading: isProductLoading,
    isError: isProductError,
  } = useProductDetails(selectedProductId);

  const { data, isLoading, isError } = useCollectionTierProducts(
    Number.isNaN(budget) ? null : budget,
  );

  // The tier's full ordered feed (all its collections, deduped by getCollectionsByBudget). The
  // country filter lives in useProductFilters — physical gifts by shippability, charity/experience
  // country-agnostic — so switching country never blanks the charity tiers ($5/$10).
  const products: Product[] = useMemo(
    () => (data?.collections ? orderCollectionProducts(data.collections) : []),
    [data?.collections],
  );

  // "Ships to" country lives in the URL (?country=) so it survives refresh, is shareable, and can be
  // read by the top-bar "Send This Collection" button (rendered in the global Drawer). Filters
  // physical gifts by shippability; charity/experience are country-agnostic.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCountry = searchParams.get("country") ?? "US";
  const setSelectedCountry = (code: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (code === "US") next.delete("country");
        else next.set("country", code);
        return next;
      },
      { replace: true },
    );

  // Display-type / Category / Cause / country filtering (shared with the single-collection view).
  const { filtered, barProps } = useProductFilters(products, selectedCountry);

  // Country-gated send (shared provider owns the international upsell modal): US/Pro+ → builder;
  // non-US + not Pro+ → upsell.
  const { sendCollection } = useCollectionsContext();
  // The CTA opens the builder, which a role that can't create campaigns is never let into.
  const { can } = usePermissions();

  const {
    renderedItems: paginatedProducts,
    hasMore,
    sentinelRef,
  } = useInfiniteScroll(filtered, 24);

  const handleViewMore = (product: Product) => {
    setSelectedProductId(product.id);
    setSlideInOpen(true);
  };

  // "Send in Collection" → country-gated: builder at Step 1 (tier + country), or the upsell.
  const handleSendInCollection = () => sendCollection(budget, selectedCountry);

  return (
    <>
      <ProductFilterBar
        {...barProps}
        selectedCountry={selectedCountry}
        onCountryChange={setSelectedCountry}
      />

      {/* Product detail slide-in — "Send in Collection" CTA opens the builder at Step 1. */}
      <ProductDetailSlideIn
        open={slideInOpen}
        product={detailedProduct}
        loading={isProductLoading}
        error={isProductError}
        mode="collections"
        ctaNote={`This gift will be included in the $${budget} Collection`}
        onClose={() => setSlideInOpen(false)}
        onSendGift={handleSendInCollection}
        hideCta={!can("create", "CAMPAIGN")}
      />

      {/* Product grid */}
      {isLoading ? (
        <ProductGridSkeleton isMobile={isMobile} />
      ) : isError ? (
        <CollectionErrorState />
      ) : filtered.length === 0 ? (
        <ProductEmptyState />
      ) : (
        <>
          <Box
            data-testid="product-grid"
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
              gap: 3,
            }}
          >
            {paginatedProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => handleViewMore(product)}
              />
            ))}
          </Box>
          {hasMore && <div ref={sentinelRef} style={{ height: "1px" }} aria-hidden="true" />}
        </>
      )}
    </>
  );
}
